import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { X, ArrowRight } from "lucide-react";
import { useStore } from "../store/StoreContext";
import { img } from "../lib/format";
import useDialogFocus from "../lib/useDialogFocus";
import { itemCodeText } from "../lib/catalogueDetails";

export default function SearchOverlay({ open, onClose }) {
  const { catalog } = useStore();
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);
  const dialogRef = useDialogFocus(open, onClose);
  const reduced = useReducedMotion();

  useEffect(() => { if (!open) setQuery(""); }, [open]);

  const results = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    return catalog
      .filter((p) =>
        [p.name, p.sku, itemCodeText(p), p.category, p.description, ...(p.materials || [])].join(" ").toLowerCase().includes(q)
      )
      .slice(0, 6);
  }, [query, catalog]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            data-testid="search-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-ink/50 z-[80]"
            onClick={onClose}
          />
          <motion.div
            data-testid="search-overlay"
            ref={dialogRef}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-label="Search the catalogue"
            initial={{ y: "-100%" }}
            animate={{ y: 0 }}
            exit={{ y: "-100%" }}
            transition={{ duration: reduced ? 0 : 0.35, ease: [0.22, 1, 0.36, 1] }}
            data-lenis-prevent
            className="search-panel fixed inset-x-0 overflow-y-auto overscroll-contain bg-white z-[90] rounded-b-[32px] shadow-2xl"
          >
            <div className="mx-auto max-w-3xl px-4 py-10 sm:py-14">
              <div className="flex items-center gap-4 border-b border-ink/20 pb-4">
                <input
                  ref={inputRef}
                  type="search"
                  inputMode="search"
                  enterKeyHint="search"
                  autoCapitalize="none"
                  autoCorrect="off"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search lamps, vases, marble…"
                  data-testid="search-input"
                  aria-label="Search the catalogue"
                  className="flex-1 min-w-0 bg-transparent text-2xl sm:text-3xl font-light outline-none placeholder:text-ink/25"
                />
                <button data-testid="search-close-button" onClick={onClose} aria-label="Close search" className="min-h-11 min-w-11 shrink-0 flex items-center justify-center p-2 hover:text-brass transition-colors duration-200">
                  <X className="w-6 h-6" strokeWidth={1.25} />
                </button>
              </div>

              {query.trim() === "" ? (
                <div className="mt-8 flex flex-wrap gap-2">
                  {["Brass", "Marble", "Lighting", "Gifting", "Vase", "Tabletop"].map((term) => (
                    <button
                      key={term}
                      data-testid={`search-suggestion-${term.toLowerCase()}`}
                      onClick={() => setQuery(term)}
                      className="pill-outline px-4 py-2 text-[11px] text-mocha hover:text-ink"
                    >
                      {term}
                    </button>
                  ))}
                </div>
              ) : results.length === 0 ? (
                <p data-testid="search-no-results" role="status" className="mt-8 text-sm text-mocha">No pieces found for “{query}”.</p>
              ) : (
                <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-6" data-testid="search-results">
                  {results.map((p) => (
                    <Link key={p.slug} to={`/product/${p.slug}`} onClick={onClose} className="group" data-testid={`search-result-${p.slug}`}>
                      <div className="bg-cream rounded-[22px] aspect-square overflow-hidden mb-3 flex items-center justify-center">
                        <img src={img(p.images[0])} alt={p.name} className="w-full h-full object-contain p-4 transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                      </div>
                      <p className="text-[14px] font-light text-center group-hover:text-brass transition-colors duration-200">{p.name}</p>
                      <p className="text-xs text-mocha mt-0.5 text-center">{p.category}</p>
                    </Link>
                  ))}
                </div>
              )}

              {results.length > 0 && (
                <Link
                  to={`/shop?search=${encodeURIComponent(query)}`}
                  onClick={onClose}
                  data-testid="search-view-all"
                  className="pill-dark mt-8"
                >
                  View all results <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                </Link>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
